import { truncateAddress } from '@kwenta/sdk/utils'
import { getAddress, isAddress } from 'ethers/lib/utils.js'
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Address } from 'wagmi'

import PencilIcon from 'assets/svg/app/pencil.svg'
import DelegationIcon from 'assets/svg/futures/delegation-icon.svg'
import MoreIcon from 'assets/svg/futures/more-icon.svg'
import NoDataIcon from 'assets/svg/futures/no-data.svg'
import RemoveIcon from 'assets/svg/futures/remove-icon.svg'
import SelectedAccountIcon from 'assets/svg/futures/selected-account-icon.svg'
import { TransactionButton } from 'components/Button/Button'
import TabButton from 'components/Button/TabButton'
import { notifyError } from 'components/ErrorNotifier'
import Input from 'components/Input/Input'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import { StyledCaretDownIcon } from 'components/Select'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import useClickOutside from 'hooks/useClickOutside'
import useENS from 'hooks/useENS'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal } from 'state/app/reducer'
import {
	addDelegate,
	fetchDelegatesForAccount,
	fetchSubAccountsForAccount,
} from 'state/futures/actions'
import { selectSelectedSubAccountAddress } from 'state/futures/common/selectors'
import {
	setNewNickname,
	setSelectedDelegationAddress,
	setSelectedSubAccountAddress,
} from 'state/futures/reducer'
import {
	selectAddressBook,
	selectNewNickname,
	selectSelectedDelegationAddress,
} from 'state/futures/selectors'
import {
	selectDelegates,
	selectSmartMarginAccount,
	selectSubAccounts,
} from 'state/futures/smartMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { resetWalletAddress } from 'state/wallet/actions'
import {
	selectAbstractionAddress,
	selectSignerWallet,
	selectSubaccountWallet,
} from 'state/wallet/selectors'
import media from 'styles/media'

import { SectionSeparator } from '../mobile'
import { CreateSmartMarginAccount } from '../Trade/ManageSmartMarginModal'

export enum DelegationSubTab {
	Delegations = 'delegations',
	SubAccounts = 'sub-accounts',
}

type Props = {
	defaultTab: DelegationSubTab
}

const DelegationTradingTab: FC<Props> = memo(({ defaultTab }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { deviceType } = useWindowSize()
	const selectedDelegationAddress = useAppSelector(selectSelectedDelegationAddress)
	const selectedSubAccountAddress = useAppSelector(selectSelectedSubAccountAddress)
	const delegatesList = useAppSelector(selectDelegates)
	const subAccountList = useAppSelector(selectSubAccounts)
	const addressBook = useAppSelector(selectAddressBook)
	const signerWallet = useAppSelector(selectSignerWallet)
	const subAccountWallet = useAppSelector(selectSubaccountWallet)
	const newNickname = useAppSelector(selectNewNickname)
	const oneClickTradingAddress = useAppSelector(selectAbstractionAddress)
	const smartMarginAccount = useAppSelector(selectSmartMarginAccount)
	const [currentTab, setCurrentTab] = useState(defaultTab)
	const [addressOrName, setAddressOrName] = useState<string>('')
	const [menuOpen, setMenuOpen] = useState(false)
	const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null)
	const { ensAddress } = useENS(addressOrName)
	const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])
	const displayedDelegatesList = useMemo(
		() => delegatesList.filter(({ address }) => address !== oneClickTradingAddress),
		[delegatesList, oneClickTradingAddress]
	)
	const [expanded, setExpanded] = useState(displayedDelegatesList.length !== 0)
	const newDelegateAddress = useMemo(() => {
		const address = ensAddress || addressOrName
		return isAddress(ensAddress || addressOrName) ? getAddress(address) : address
	}, [addressOrName, ensAddress])
	const { ref } = useClickOutside(() => setMenuOpen(false))

	const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded])

	const onAddressChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setAddressOrName(e.target.value),
		[]
	)

	const onNicknameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => dispatch(setNewNickname(e.target.value)),
		[dispatch]
	)

	const handleAddDelegate = useCallback(() => {
		const isAddressExist = delegatesList.some(
			({ address }) => address === selectedDelegationAddress
		)
		if (!isAddressExist) {
			try {
				dispatch(addDelegate())
			} catch (error) {
				notifyError('Failed to add new delegate', error)
			}
		} else {
			notifyError('This address already exists in your list')
		}
	}, [delegatesList, dispatch, selectedDelegationAddress])

	const handleManage = useCallback(
		(event: React.MouseEvent, address: string, type: 'delegate' | 'subaccount') => {
			const x = event.clientX
			const y = event.clientY
			dispatch(
				type === 'delegate'
					? setSelectedDelegationAddress(address)
					: setSelectedSubAccountAddress(address)
			)
			setMenuPosition({ x, y })
			setMenuOpen(true)
		},
		[dispatch]
	)

	useEffect(() => {
		dispatch(fetchDelegatesForAccount())
		dispatch(fetchSubAccountsForAccount())
		dispatch(setNewNickname(''))
		setAddressOrName('')
	}, [addressBook, dispatch])

	useEffect(() => setExpanded(displayedDelegatesList.length !== 0), [displayedDelegatesList])

	const handleRemoveDelegate = useCallback(() => {
		if (selectedDelegationAddress) {
			dispatch(setOpenModal('remove_delegate'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a delegate to remove')
		}
	}, [dispatch, selectedDelegationAddress])

	const handleRenameDelegate = useCallback(() => {
		if (selectedDelegationAddress) {
			dispatch(setOpenModal('rename_delegate'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a delegate to rename')
		}
	}, [dispatch, selectedDelegationAddress])

	const handleRenameSubAccount = useCallback(() => {
		if (selectedSubAccountAddress) {
			dispatch(setOpenModal('rename_subaccount'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a subaccount to rename')
		}
	}, [dispatch, selectedSubAccountAddress])

	const handleSelectedSubAccount = useCallback(
		(address: string) => {
			if (address === selectedSubAccountAddress) {
				dispatch(resetWalletAddress({ address: signerWallet, selectedType: 'signer' }))
				dispatch(setSelectedSubAccountAddress(''))
			} else {
				dispatch(setSelectedSubAccountAddress(address))
				dispatch(resetWalletAddress({ address: address as Address, selectedType: 'sub_account' }))
			}
		},
		[dispatch, signerWallet, selectedSubAccountAddress]
	)

	return (
		<>
			<TabButtons>
				<TabButton
					variant="noOutline"
					title={t('futures.market.trade.margin.modal.delegation.delegations-tabs')}
					onClick={() => setCurrentTab(DelegationSubTab.Delegations)}
					active={currentTab === DelegationSubTab.Delegations}
				/>
				<TabButton
					variant="noOutline"
					title={t('futures.market.trade.margin.modal.delegation.sub-accounts-tabs')}
					onClick={() => setCurrentTab(DelegationSubTab.SubAccounts)}
					active={currentTab === DelegationSubTab.SubAccounts}
				/>
			</TabButtons>
			<Container>
				<TabPanel name={DelegationSubTab.Delegations} activeTab={currentTab} fullHeight={!isMobile}>
					<FlexDivCol rowGap="20px">
						{smartMarginAccount ? (
							<>
								<FlexDivRow justifyContent="flex-start" columnGap="10px" onClick={toggleExpanded}>
									<Body color="secondary">
										{t('futures.market.trade.margin.modal.delegation.add-new-delegate')}
									</Body>
									{isMobile && <StyledCaretDownIcon $flip={expanded} />}
								</FlexDivRow>
								{(!isMobile || !expanded) && (
									<>
										<StyledInput
											disabled={!smartMarginAccount}
											value={newDelegateAddress}
											onChange={onAddressChange}
											placeholder="0x"
										/>
										<Body size="small" color="secondary">
											{t('futures.market.trade.margin.modal.delegation.new.input-label')}
										</Body>
										<NicknameInput
											placeholder={t('futures.market.trade.margin.modal.delegation.new.optional')}
											value={newNickname}
											onChange={onNicknameChange}
										/>
										<TransactionButton
											fullWidth
											disabled={!isAddress(newDelegateAddress)}
											onClick={handleAddDelegate}
										>
											{t('futures.market.trade.margin.modal.delegation.add-delegate-button')}
										</TransactionButton>
										<MessageContainer>
											<TextList>
												<li>
													{t('futures.market.trade.margin.modal.delegation.new.warning-copy-1')}
												</li>
												<li>
													{t('futures.market.trade.margin.modal.delegation.new.warning-copy-2')}
												</li>
											</TextList>
										</MessageContainer>
									</>
								)}
							</>
						) : (
							<CreateSmartMarginAccount />
						)}
						<StyledSectionSeparator />
						<Body>{t('futures.market.trade.margin.modal.delegation.manage-delegations')}</Body>
						<ContainerWithInfiniteScroll rowGap="30px" height={isMobile ? '250px' : '110px'}>
							{displayedDelegatesList.length === 0 ? (
								<FlexDivColCentered rowGap={isMobile ? '20px' : '10px'}>
									<NoDataIcon height={isMobile ? 'initial' : '80'} />
									<Body color="secondary">
										{t('futures.market.trade.margin.modal.delegation.no-delegations')}
									</Body>
								</FlexDivColCentered>
							) : (
								displayedDelegatesList.map(({ address, nickname }) => (
									<FlexDivRowCentered key={address} justifyContent="space-between">
										<FlexDivRow justifyContent="flexStart" columnGap="12px">
											<DelegationIcon height={32} width={32} />
											<FlexDivCol rowGap="5px">
												<Body weight="bold" color="primary">
													{nickname ||
														t('futures.market.trade.margin.modal.delegation.default-name')}
												</Body>
												<Body color="secondary">{truncateAddress(address)}</Body>
											</FlexDivCol>
										</FlexDivRow>
										<StyeldMoreIcon
											height={24}
											width={24}
											onClick={(event: React.MouseEvent) =>
												handleManage(event, address, 'delegate')
											}
										/>
									</FlexDivRowCentered>
								))
							)}
						</ContainerWithInfiniteScroll>
					</FlexDivCol>
				</TabPanel>
				<TabPanel name={DelegationSubTab.SubAccounts} activeTab={currentTab}>
					<FlexDivCol rowGap="20px">
						<Body>{t('futures.market.trade.margin.modal.delegation.manage-sub-accounts')}</Body>
						<ContainerWithInfiniteScroll rowGap="30px" height={isMobile ? '300px' : '440px'}>
							{subAccountList.length === 0 ? (
								<NoDataContainer rowGap="30px">
									<NoDataIcon />
									<Body>{t('futures.market.trade.margin.modal.delegation.no-sub-accounts')}</Body>
								</NoDataContainer>
							) : (
								subAccountList.map(({ address, nickname }) => (
									<SubAccountRowContainer key={address} justifyContent="space-between">
										<SubAccountDetails
											justifyContent="flexStart"
											columnGap="12px"
											onClick={() => handleSelectedSubAccount(address)}
										>
											{address === subAccountWallet ? (
												<SelectedAccountIcon height={32} width={32} />
											) : (
												<DelegationIcon height={32} width={32} />
											)}
											<FlexDivCol rowGap="5px">
												<Body weight="bold" color="primary">
													{nickname ||
														t('futures.market.trade.margin.modal.delegation.default-account-name')}
												</Body>
												<Body color="secondary">{truncateAddress(address)}</Body>
											</FlexDivCol>
										</SubAccountDetails>
										<StyeldMoreIcon
											height={24}
											width={24}
											onClick={(event: React.MouseEvent) =>
												handleManage(event, address, 'subaccount')
											}
										/>
									</SubAccountRowContainer>
								))
							)}
						</ContainerWithInfiniteScroll>
					</FlexDivCol>
				</TabPanel>
				{menuOpen && menuPosition && (
					<MenuContainer
						ref={ref}
						top={`${menuPosition.y + 20}px`}
						left={`${menuPosition.x - 150}px`}
					>
						<FlexDivCol>
							<MenuItem
								columnGap="8px"
								justifyContent="flexStart"
								onClick={
									currentTab === DelegationSubTab.Delegations
										? handleRenameDelegate
										: handleRenameSubAccount
								}
							>
								<StyledPencilIcon height={12} width={12} />
								<Body color="primary">
									{t('futures.market.trade.margin.modal.delegation.rename.menu-title')}
								</Body>
							</MenuItem>
							{currentTab === DelegationSubTab.Delegations && (
								<MenuItem columnGap="8px" justifyContent="flexStart" onClick={handleRemoveDelegate}>
									<RemoveIcon />
									<Body color="negative">
										{t('futures.market.trade.margin.modal.delegation.remove.menu-title')}
									</Body>
								</MenuItem>
							)}
						</FlexDivCol>
					</MenuContainer>
				)}
			</Container>
		</>
	)
})

const Container = styled.div`
	${media.lessThan('lg')`
		height: calc(100 - 65px);
		overflow-y: auto;
	`}
`

const NicknameInput = styled(Input)`
	font-size: 13px;
`
const MessageContainer = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.button.yellow.text};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: left;
	font-size: 13px;
	padding: 0px 10px;
	border: 1px solid rgba(239, 104, 104, 0);
	background: ${(props) => props.theme.colors.selectedTheme.button.yellow.fill};
	border-radius: 8px;
	cursor: default;
	white-space: pre-line;
`

const TextList = styled.ul`
	list-style-position: inside;
	list-style: initial;
	margin: initial;
	padding: 10px 20px;
	li:not(:last-child) {
		margin-bottom: 10px;
	}
`

const StyledPencilIcon = styled(PencilIcon)`
	fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.tertiary};
`
const NoDataContainer = styled(FlexDivColCentered)`
	margin: 50px 0;
`

const SubAccountRowContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	transition: all 0.1s ease-in-out;
	:hover {
		opacity: 0.8;
	}
`

const ContainerWithInfiniteScroll = styled(FlexDivCol)<{ height?: string }>`
	height: ${(props) => props.height || '250px'};
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: transparent transparent;

	&::-webkit-scrollbar {
		width: 0;
	}
`

const MenuItem = styled(FlexDivRowCentered)`
	min-width: 160px;
	transition: all 0.1s ease-in-out;
	:hover {
		opacity: 0.8;
	}
	padding: 9px 15px;
	cursor: pointer;
`

const MenuContainer = styled.div<{ top: string; left: string }>`
	position: fixed;
	top: ${(props) => props.top};
	left: ${(props) => props.left};
	border-radius: 8px;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	box-shadow: ${(props) => props.theme.colors.selectedTheme.button.primary.textShadow};
	padding: 8px 0px;
`

const StyeldMoreIcon = styled(MoreIcon)`
	cursor: pointer;
	transition: all 0.1s ease-in-out;
	:hover {
		border-radius: 100px;
		border: 1px solid
			${(props) => props.theme.colors.selectedTheme.newTheme.pill.gray.outline.border};
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.button.default.hover.background};
	}
`

const StyledSectionSeparator = styled(SectionSeparator)`
	margin: 0;
`

const StyledInput = styled(Input)`
	font-size: 15px;
	font-family: ${(props) => props.theme.fonts.mono};

	${media.lessThan('md')`
		font-size: 13px;
	`}
`

const TabButtons = styled.div`
	display: flex;
	gap: 25px;
	margin-bottom: 25px;
`

const SubAccountDetails = styled(FlexDivRow)`
	width: 100%;
`

export default DelegationTradingTab
